import { scrollToPosition } from './utils/scroll';

const AUTOPLAY_INTERVAL_MS = 6000;
const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * @typedef { object } ImageCarousel
 * @property { HTMLElement } block The block element.
 * @property { HTMLElement } track The scrollable list of slides.
 * @property { HTMLElement[] } slides The slide elements.
 * @property { HTMLElement[] } dots The dot buttons, one per slide.
 * @property { HTMLElement | null } autoplayToggle The play/pause button, if autoplay is enabled for the block.
 * @property { number } index Index of the current slide.
 * @property { boolean } autoplayRunning Whether the visitor wants the carousel to rotate.
 * @property { Set<string> } autoplaySuspensions Reasons the rotation is temporarily on hold (hover, focus, off screen, hidden tab).
 * @property { number | null } autoplayTimer Interval handle while rotating.
 */

/**
 * Initializes all image carousel blocks on the page.
 */
export default function initImageCarousels() {
  document.querySelectorAll('.image-carousel-block').forEach((block) => initImageCarousel(block));
}

/**
 * @param { HTMLElement } block The image carousel block element.
 */
function initImageCarousel(block) {
  const track = block.querySelector('.track');
  const slides = track ? Array.from(track.children) : [];

  if(!track || slides.length < 2) {
    return;
  }

  /** @type { ImageCarousel } */
  const carousel = {
    block,
    track,
    slides,
    dots: Array.from(block.querySelectorAll('.dot')),
    autoplayToggle: block.querySelector('.autoplay-toggle'),
    index: 0,
    autoplayRunning: false,
    autoplaySuspensions: new Set(),
    autoplayTimer: null,
  };

  block.querySelector('.carousel-arrow.start')?.addEventListener('click', () => goToSlide(carousel, carousel.index - 1, true));
  block.querySelector('.carousel-arrow.end')?.addEventListener('click', () => goToSlide(carousel, carousel.index + 1, true));
  carousel.dots.forEach((dot, dotIndex) => dot.addEventListener('click', () => goToSlide(carousel, dotIndex, true)));

  track.addEventListener('scroll', () => updateCurrentSlide(carousel), { passive: true });
  track.addEventListener('touchstart', () => stopAutoplay(carousel), { passive: true });

  if(carousel.autoplayToggle) {
    initAutoplay(carousel);
  }
}

/**
 * Wires up the play/pause button and the conditions that put rotation on hold.
 * @param { ImageCarousel } carousel The carousel.
 */
function initAutoplay(carousel) {
  const { block, track, autoplayToggle } = carousel;

  autoplayToggle.addEventListener('click', () => {
    if(carousel.autoplayRunning) {
      stopAutoplay(carousel);
      return;
    }

    // Pressing play is explicit intent, so it has to visibly start rotating. Lift the
    // holds the press itself leaves behind: focus stays on the button, and a tap counts
    // as hovering the block until the visitor taps somewhere else.
    resumeAutoplay(carousel, 'focus');
    resumeAutoplay(carousel, 'hover');
    startAutoplay(carousel);
  });

  // Pointer events rather than mouseenter/mouseleave: a tap emulates a mouse enter that
  // never pairs with a leave, which would hold the rotation for the rest of the visit.
  block.addEventListener('pointerenter', (event) => {
    if(event.pointerType === 'mouse') {
      suspendAutoplay(carousel, 'hover');
    }
  });
  block.addEventListener('pointerleave', (event) => {
    if(event.pointerType === 'mouse') {
      resumeAutoplay(carousel, 'hover');
    }
  });
  // Focus anywhere inside the carousel holds the rotation, the play/pause button included:
  // tabbing onto it must not set the images moving under the visitor.
  block.addEventListener('focusin', () => suspendAutoplay(carousel, 'focus'));
  block.addEventListener('focusout', (event) => {
    if(!block.contains(event.relatedTarget)) {
      resumeAutoplay(carousel, 'focus');
    }
  });

  document.addEventListener('visibilitychange', () => {
    document.hidden ? suspendAutoplay(carousel, 'hidden-tab') : resumeAutoplay(carousel, 'hidden-tab');
  });

  const visibilityObserver = new IntersectionObserver(([entry]) => {
    entry.isIntersecting ? resumeAutoplay(carousel, 'off-screen') : suspendAutoplay(carousel, 'off-screen');
  }, { threshold: 0.5 });
  visibilityObserver.observe(track);

  reducedMotionQuery.addEventListener('change', () => {
    if(reducedMotionQuery.matches) {
      stopAutoplay(carousel);
    }
  });

  if(block.dataset.autoplay === 'true' && !reducedMotionQuery.matches) {
    startAutoplay(carousel);
  } else {
    stopAutoplay(carousel);
  }
}

/**
 * Scrolls to a slide, wrapping around at either end.
 * @param { ImageCarousel } carousel The carousel.
 * @param { number } index Index of the slide to show.
 * @param { boolean } byVisitor True when the visitor asked for the change; stops any rotation.
 */
function goToSlide(carousel, index, byVisitor) {
  const { track, slides } = carousel;
  const wrappedIndex = (index + slides.length) % slides.length;

  if(byVisitor) {
    stopAutoplay(carousel);
  }

  scrollToPosition(track, slides[wrappedIndex].offsetLeft, {
    direction: 'horizontal',
    scrollBehaviour: reducedMotionQuery.matches ? 'auto' : 'smooth'
  });
}

/**
 * Derives the current slide from the scroll position and marks its dot.
 * @param { ImageCarousel } carousel The carousel.
 */
function updateCurrentSlide(carousel) {
  const { track, slides, dots } = carousel;
  const slideWidth = slides[0].offsetWidth || 1;
  const index = Math.min(slides.length - 1, Math.max(0, Math.round(track.scrollLeft / slideWidth)));

  if(index === carousel.index) {
    return;
  }

  carousel.index = index;
  dots.forEach((dot, dotIndex) => dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false'));
}

/**
 * @param { ImageCarousel } carousel The carousel.
 */
function startAutoplay(carousel) {
  carousel.autoplayRunning = true;
  carousel.autoplayToggle.setAttribute('data-is-playing', 'true');
  carousel.track.setAttribute('aria-live', 'off');
  syncAutoplayTimer(carousel);
}

/**
 * @param { ImageCarousel } carousel The carousel.
 */
function stopAutoplay(carousel) {
  if(!carousel.autoplayToggle) {
    return;
  }

  carousel.autoplayRunning = false;
  carousel.autoplayToggle.setAttribute('data-is-playing', 'false');
  carousel.track.setAttribute('aria-live', 'polite');
  syncAutoplayTimer(carousel);
}

/**
 * Puts rotation on hold for a reason, without changing what the visitor asked for.
 * @param { ImageCarousel } carousel The carousel.
 * @param { string } reason Why the rotation is on hold.
 */
function suspendAutoplay(carousel, reason) {
  carousel.autoplaySuspensions.add(reason);
  syncAutoplayTimer(carousel);
}

/**
 * Lifts a hold on the rotation; rotation continues only if no other hold remains.
 * @param { ImageCarousel } carousel The carousel.
 * @param { string } reason The hold to lift.
 */
function resumeAutoplay(carousel, reason) {
  carousel.autoplaySuspensions.delete(reason);
  syncAutoplayTimer(carousel);
}

/**
 * Runs the interval only while the visitor wants rotation and nothing has put it on hold.
 * @param { ImageCarousel } carousel The carousel.
 */
function syncAutoplayTimer(carousel) {
  const shouldRun = carousel.autoplayRunning && carousel.autoplaySuspensions.size === 0;

  if(shouldRun && carousel.autoplayTimer === null) {
    carousel.autoplayTimer = window.setInterval(() => goToSlide(carousel, carousel.index + 1, false), AUTOPLAY_INTERVAL_MS);
  } else if(!shouldRun && carousel.autoplayTimer !== null) {
    window.clearInterval(carousel.autoplayTimer);
    carousel.autoplayTimer = null;
  }
}
